import React, { createContext, useState, useContext, useEffect } from "react";
import { InvokeAPI } from "../apis/API";
import { getUserMgmtBase } from "../config/apiBase";
import { registerAccessTokenRefreshHandler } from "../apis/tokenEvents";

const LEGACY_USER_KEY = "user";

// Create AuthContext
const AuthContext = createContext();

// Custom Hook to use AuthContext
export const useAuth = () => useContext(AuthContext);

export const isParent = () => {
  const { user } = useAuth();
  let found = false;
  user.roles.forEach((element) => {
    if (element.toLowerCase() === "parent") {
      found = true;
    }
  });
  return found;
};

export const isTeacher = () => {
  const { user } = useAuth();
  let found = false;
  user.roles.forEach((element) => {
    if (element.toLowerCase() === "teacher") {
      found = true;
    }
  });
  return found;
};

export const isStudent = () => {
  const { user } = useAuth();
  let found = false;
  if (user === undefined || user.roles == undefined) {
    return false;
  }
  user.roles.forEach((element) => {
    if (element.toLowerCase() === "student") {
      found = true;
    }
  });
  return found;
};

export const isActive = () => {
  const { user } = useAuth();
  if (user === undefined) {
    return false;
  }
  return user["isActive"];
};

export const isVerified = () => {
  const { user } = useAuth();
  if (user === undefined) {
    return false;
  }
  return user["isVerified"];
};

export const isExpired = () => {
  const { user } = useAuth();
  if (user === undefined) {
    return false;
  } else if (user["isActive"]) {
    return false;
  } else if (user["accountState"] == "expired") {
    return true;
  }
  return false;
};

export const isTrial = () => {
  const { user } = useAuth();
  if (user === undefined) {
    return false;
  } else if (user["isActive"]) {
    return false;
  } else if (user["accountState"] == "trial") {
    return true;
  }
  return false;
};

export const isAuthenticated = () => {
  const { user } = useAuth();
  if (!user) {
    return false;
  }

  if (user["state"] == "loggedout") {
    return false;
  }

  /*
  if (user.role === "admin") {
    return true
  }

  const routeRoles = RouteRoles
  const currentPath = window.location.pathname
  for (let route in routeRoles) {
    if (route === currentPath ) {
      routeRoles[route].forEach(element => {
        if (user.role === element) {
        } else {
          return false;
        }
      });
    }
  }
    */
  return true;
};

// AuthProvider Component
export const AuthProvider = ({ children }) => {
  const getUserData = async (userId, token) => {
    const uinfo = await InvokeAPI(
      `${getUserMgmtBase()}/v1/user/info`,
      { ID: userId },
      "POST",
      null,
      true,
      token
    );
    if (
      uinfo["unauthenticated"] == true ||
      uinfo["unauthorized"] == true ||
      uinfo["othererror"] == true
    ) {
      return { state: "loggedout" };
    }
    var roles = [];
    if (uinfo.response["IsStudent"] === true) {
      roles = ["student"];
    } else {
      roles = ["parent"];
    }
    return {
      state: "loggedin",
      token: token,
      id: userId,
      username: uinfo.response["Username"],
      email: uinfo.response["EmailId"],
      fname: uinfo.response["Fname"],
      lname: uinfo.response["Lname"],
      phone: uinfo.response["Phone"],
      isActive: uinfo.response["IsActive"],
      isVerified: uinfo.response["IsVerified"],
      roles: roles,
      referralCode: uinfo.response["ReferralCode"],
      accountState: uinfo.response["AccountState"],
    };
  };

  const [user, setUser] = useState(null);

  useEffect(() => {
    registerAccessTokenRefreshHandler(({ token, id }) => {
      setUser((prev) => {
        if (!prev || prev.state !== "loggedin") {
          return prev;
        }
        return { ...prev, token, id };
      });
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const attemptRefresh = async () => {
      const r = await fetch(`${getUserMgmtBase()}/v1/user/session/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      if (!r.ok) {
        return null;
      }
      const j = await r.json();
      const token = j.Token ?? j.token;
      const id = j.ID ?? j.id;
      if (!token || !id) {
        return null;
      }
      return { token, id };
    };

    (async () => {
      const restored = await attemptRefresh();
      if (cancelled) {
        return;
      }
      if (restored) {
        const data = await getUserData(restored.id, restored.token);
        if (!cancelled) {
          setUser(data);
        }
        return;
      }

      try {
        const legacy = sessionStorage.getItem(LEGACY_USER_KEY);
        if (legacy) {
          const sessData = JSON.parse(legacy);
          if (sessData.id && sessData.token) {
            const u = await getUserData(sessData.id, sessData.token);
            sessionStorage.removeItem(LEGACY_USER_KEY);
            if (!cancelled) {
              setUser(u.state === "loggedin" ? u : { state: "loggedout" });
            }
            return;
          }
        }
      } catch (e) {
        sessionStorage.removeItem(LEGACY_USER_KEY);
      }

      if (!cancelled) {
        setUser({ state: "loggedout" });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (username, password) => {
    const ltoken = await InvokeAPI(
      `${getUserMgmtBase()}/v1/user/authenticate`,
      { Username: username, Password: password },
      "POST",
      null,
      false
    );

    if (ltoken["notfound"] == true) {
      return 1;
    }
    if (ltoken.invalidargument == true) {
      return 2;
    }
    if (ltoken.rateLimitExceeded == true) {
      return { code: 4, message: ltoken.errorMessage };
    }
    if (ltoken.permissionDenied == true) {
      return { code: 5, message: ltoken.errorMessage };
    }

    const authResp = ltoken["response"];
    if (authResp["Token"] !== "") {
      const userData = await getUserData(authResp["ID"], authResp["Token"]);
      if (userData["state"] == "loggedout") {
        return 3;
      }

      sessionStorage.removeItem(LEGACY_USER_KEY);

      setUser(userData);

      return 0;
    }

    return 3;
  };

  const logout = async (userData) => {
    setUser({ state: "loggedout" });
    sessionStorage.removeItem(LEGACY_USER_KEY);
    await InvokeAPI(
      `${getUserMgmtBase()}/v1/user/logout`,
      {},
      "POST",
      null,
      true,
      userData["token"]
    );
  };

  const updateUser = async (newData) => {
    const uinfo = await InvokeAPI(
      `${getUserMgmtBase()}/v1/user/update`,
      {
        ID: newData["id"],
        Username: newData["username"],
        Fname: newData["fname"],
        Lname: newData["lname"],
        Phone: newData["phone"],
      },
      "POST",
      null,
      true,
      user["token"]
    );
    if (
      uinfo["unauthenticated"] == true ||
      uinfo["unauthorized"] == true ||
      uinfo["othererror"] == true
    ) {
      setUser({ state: "loggedout" });
    } else {
      if (uinfo.alreadyExists) {
        alert("username already exists");
      } else {
        const userData = await getUserData(user["id"], user["token"]);
        setUser(userData);
      }
    }
  };

  const refreshUser = async () => {
    const userData = await getUserData(user["id"], user["token"]);
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, updateUser, refreshUser, login, logout }}>
      {user && user["state"] == "loggedout" && children}
      {user && user["state"] == "loggedin" && user["id"] && children}
    </AuthContext.Provider>
  );
};
